/**
 * TutorialContext - Global state management for interactive tutorial
 * Persists to Supabase onboarding_state table with debounced writes
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
// DTC-L: route tutorial started_at write through clock.
import { clock } from '@/utils/clock';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { type OnboardingStep } from './OnboardingSteps';
import { checkStepGate, type GateCheckContext } from './OnboardingGates';
import { usePermissions } from '@/hooks/usePermissions';
import {
  resolveExperienceSteps,
  stepsForActiveExperience,
} from '@/lib/onboarding/resolveExperienceSteps';
import {
  chooseActiveExperience,
  resolveMultiRoleExperiences,
  type MultiRoleExperiences,
} from '@/lib/onboarding/multiRoleExperiences';
import type { AuthorityRole, WorkRole } from '@/lib/roles/multiRole';
import {
  EXPERIENCE_LABEL,
  readMembership,
  resolveOnboardingExperience,
  type AppRole,
  type KnownAppRole,
  type ExperienceResolution,
  type OnboardingExperience,
  type UnresolvedReason,
} from '@/lib/onboarding/onboardingExperience';

interface OnboardingState {
  id?: string;
  user_id: string;
  shop_id: string;
  flow: OnboardingExperience;
  current_step: number;
  completed: boolean;
  dismissed: boolean;
  steps_completed: string[];
  last_page?: string;
  started_at?: string;
  updated_at?: string;
}

/**
 * Why a start did not happen.
 *
 * Every one of these is a state in which writing a fresh `current_step: 0`
 * row would destroy or fabricate something:
 *
 *   no_session          nobody is signed in to a shop; there is no row to own
 *   experience_unknown  the membership read has not settled, or settled on
 *                       "cannot tell" — starting would pick a track for
 *                       somebody whose role we do not know
 *   progress_unread     the stored progress read has not finished
 *   progress_failed     the stored progress read FAILED — an upsert here
 *                       restarts a half-finished tutorial because the
 *                       database had a bad moment
 *   no_content          nothing is authored for this person's experience
 *   route_blocked       content IS authored, and the router admits this role
 *                       to none of the pages it happens on. A DIFFERENT fact
 *                       from `no_content`, and it used to be collapsed into
 *                       it here — telling a dispatcher nothing was written
 *                       for their role when the lessons exist and the doors
 *                       are what is shut
 *   save_failed         the first write did not land; the tutorial exists
 *                       only in memory and will not survive a refresh
 */
export type StartRefusalReason =
  | 'no_session'
  | 'experience_unknown'
  | 'progress_unread'
  | 'progress_failed'
  | 'no_content'
  | 'route_blocked'
  | 'save_failed';

/**
 * The outcome of a start.
 *
 * `message` is present when the resolver had something a person should
 * actually read — the route-blocked and no-content explanations. Carrying it
 * here means first-use delivery can SAY it before spending the invitation,
 * rather than silently deciding on somebody's behalf.
 */
export type StartTutorialResult =
  | { status: 'started'; flow: OnboardingExperience }
  | { status: 'refused'; reason: StartRefusalReason; message?: string };

export type SaveStatus = 'idle' | 'pending' | 'saved' | 'error';

/**
 * What may be OFFERED to this person, resolved once, in one place.
 *
 * `TopBar` used to build its own: it read `membership` straight from
 * `AuthContext`, called `resolveOnboardingExperience` itself, and ran
 * `stepsForActiveExperience` to pick a destination — while `TutorialContext`
 * independently read the canonical membership row and owned the resolved
 * experience. Two resolutions from two sources, which agree only while
 * nothing is changing. During a shop switch or a role change they disagree,
 * and the menu would offer one walkthrough while the start produced another.
 *
 * Now there is one. The menu decides whether to render it and what to do on
 * click; it does not decide who the person is or what they are taught.
 */
export interface OnboardingOffer {
  experience: OnboardingExperience;
  /** Human name for the experience, from the single label authority. */
  label: string;
  /** Where the FIRST delivered lesson happens — never a hardcoded guess. */
  firstStepPage: string;
  /** Length of the delivered sequence. */
  stepCount: number;
}

/**
 * The row a queued write belongs to.
 *
 * Captured when the write is QUEUED, never read from the closure when it
 * fires. The debounce means up to half a second passes between the two, and
 * an active shop can change inside it — which is exactly how a manager's
 * step 4 was once written into whichever shop happened to be current when
 * the timer expired.
 */
interface SaveScope {
  userId: string;
  shopId: string;
  flow: OnboardingExperience;
}

const scopeKey = (scope: SaveScope | null): string | null =>
  scope ? `${scope.userId}::${scope.shopId}::${scope.flow}` : null;

interface TutorialContextType {
  // State
  isActive: boolean;
  flow: OnboardingExperience | null;
  currentStep: number;
  currentStepData: OnboardingStep | null;
  completed: boolean;
  dismissed: boolean;
  stepsCompleted: string[];
  isLoading: boolean;
  isMinimized: boolean;
  isHelpMode: boolean;
  showHelpMenu: boolean;
  /**
   * CORE-BL: which track this person resolved to, or why none did.
   *
   * Exposed so surfaces can say something true when there is no tour —
   * "we could not check your role", "there is nothing written for your role
   * yet" — instead of opening an empty shell or falling silent. Responsibility
   * 8 is about what the product SAYS when it has nothing, and it can only say
   * it if the reason reaches the UI.
   */
  experienceResolution: ExperienceResolution | null;
  /**
   * EVERY walkthrough this person should be offered, because one person can
   * now do more than one job.
   *
   * `experienceResolution` above still names the single legacy resolution and
   * is untouched, so nothing that reads it changes behaviour. This is the set,
   * and each member keeps its OWN `onboarding_state` row: somebody who is both
   * a technician and a service advisor finishes one without it claiming
   * anything about the other.
   *
   * `null` while the scope is unresolved, for the same reason
   * `experienceResolution` is.
   */
  multiRoleExperiences: MultiRoleExperiences | null;
  /**
   * Whether the progress shown has actually reached the database.
   *
   * `'error'` means the person's place is real in memory and was not
   * written. Surfaces must not promise durability the save did not achieve.
   *
   * SCOPED. This describes the row for the person, shop and flow currently on
   * screen and nothing else. A write for shop A that lands (or fails) after
   * somebody has switched to shop B leaves this alone, because "saved" under
   * shop B would be a claim about a row that was never written.
   */
  saveStatus: SaveStatus;
  /** Whether stored progress could be established at all. */
  progressRead: 'loading' | 'loaded' | 'failed';
  /**
   * THE sequence this person is being walked through.
   *
   * Exposed because the visible shell used to rebuild it from `flow` with
   * `getStepsForFlow`, which returns the RAW track. A dispatcher's context
   * walked 5 filtered lessons while the checklist listed 7 — so the badge
   * read 2/7, the checklist offered intake and custody that had been
   * filtered out, and clicking item 6 handed a raw index to a jump that
   * interprets filtered indexes. Every consumer now reads this array, so
   * there is one index domain and one total.
   */
  activeSteps: OnboardingStep[];
  /** Length of `activeSteps`. The only correct progress denominator. */
  totalSteps: number;
  /**
   * The experience this person resolves to, whether or not they have started
   * it. Null while unresolved.
   */
  resolvedExperience: OnboardingExperience | null;
  /**
   * The walkthrough that may be offered to this person right now, or null.
   *
   * Null while unresolved, and null when nothing is deliverable — so a
   * surface that renders it cannot advertise an empty shell or a page the
   * router refuses.
   */
  onboardingOffer: OnboardingOffer | null;
  /**
   * Why there is no offer, when there is none — the resolver's own reason,
   * so a surface can say something true instead of falling silent.
   */
  offerUnavailableReason: UnresolvedReason | null;
  /**
   * What to CALL the walkthrough on screen — one label authority.
   *
   * Every surface used to name it with its own ternary on `flow`:
   * `manager` said "Setup Tutorial", legacy `technician` said "Technician
   * Setup", and EVERYTHING ELSE fell through to "User Tutorial". So a
   * technician on `technician_core_v1`, an advisor on `foh_service_writer`
   * and an org admin on `org_overview_v1` were all told they were doing the
   * "User Tutorial", and were congratulated at the end for having
   * "configured your shop".
   *
   * Names the row on screen when there is one, and otherwise what they would
   * be offered.
   */
  experienceLabel: string | null;
  
  // Methods
  /**
   * Begin THIS PERSON'S experience — and say whether it actually began.
   *
   * Takes no flow, deliberately. The previous signature accepted one and only
   * checked that the person had *some* resolved experience, so a technician
   * could be started on `manager`; the manager library was then route-
   * filtered for them and that shortened sequence persisted under the durable
   * `manager` key. One key must mean one exact ordered sequence, and a
   * caller-supplied key breaks that in the database rather than on screen.
   *
   * Callers act on the answer: the account-menu entry navigates to the first
   * lesson, and first-use delivery spends the one-shot invitation. Both used
   * to do that unconditionally, because this returned `Promise<void>` and a
   * refusal was indistinguishable from a start.
   */
  startResolvedTutorial: () => Promise<StartTutorialResult>;
  /**
   * Core-100 R4 CORE-BL (OD-14 multi-role): make ANOTHER of this person's
   * offered walkthroughs the active one — an owner who also turns wrenches
   * opens on `manager` and can switch to `technician_core_v1`. Refused (false)
   * for an experience the person is not offered; never a free-form flow key.
   * Each experience keeps its own progress row.
   */
  switchExperience: (experience: OnboardingExperience) => boolean;
  nextStep: () => Promise<void>;
  previousStep: () => Promise<void>;
  skipStep: () => Promise<void>;
  jumpToStep: (stepIndex: number) => Promise<void>;
  /**
   * Finish the current walkthrough: last step recorded, `completed` true,
   * `dismissed` untouched, written against the flow on screen.
   */
  completeTutorial: () => Promise<void>;
  /**
   * The completion notice to show, or null. Owned here so it outlives the
   * step — and the Coachmark — that produced it.
   */
  completionNotice: { flow: OnboardingExperience; label: string } | null;
  /** Dismiss the completion notice. Changes no stored progress. */
  acknowledgeCompletion: () => void;
  /** Whether the floating help affordance is hidden for this session. */
  helpButtonHidden: boolean;
  /**
   * Hide the floating help button for this session only.
   *
   * Not a dismissal: `completed` stays true, `dismissed` stays false, and the
   * walkthrough remains available from the account menu.
   */
  hideHelpButton: () => void;
  dismissTutorial: () => Promise<void>;
  resumeTutorial: () => void;
  /**
   * Replay THIS PERSON'S experience from the beginning.
   *
   * Also takes no flow. It used to accept one, which is how the help menu
   * offered every completed person "Replay Shop Setup" and "Replay Daily
   * Operations" regardless of what they had actually been taught.
   */
  replayTutorial: () => Promise<StartTutorialResult>;
  checkCurrentGate: () => Promise<boolean>;
  markStepComplete: (stepId: string) => Promise<void>;
  toggleMinimize: () => void;
  openHelpMenu: () => void;
  closeHelpMenu: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, activeShop } = useAuth();
  
  /**
   * The stored row, exactly as it was read or written.
   *
   * NOT read directly below — `state` is the masked view of it. See
   * `stateIsCurrentForAuthScope`.
   */
  const [rawState, setState] = useState<OnboardingState | null>(null);
  const [rawIsLoading, setIsLoading] = useState(true);
  const [isMinimized, setIsMinimized] = useState(() => {
    return localStorage.getItem('tutorial-minimized') === 'true';
  });
  const [showHelpMenu, setShowHelpMenu] = useState(false);

  /**
   * The "you finished it" notice, owned ABOVE the step that produced it.
   *
   * It used to be `showCompletionDialog` inside `Coachmark`. Finishing sets
   * `completed`, which makes `isActive` false, and `GlobalTutorialBar` renders
   * coachmarks under `isActive && steps.map(...)` — so the parent unmounted
   * every Coachmark the instant the walkthrough was completed, taking the
   * dialog's local state with it. The person pressed Complete and the screen
   * simply went quiet.
   *
   * Guarding inside Coachmark could not fix that; a child cannot survive its
   * parent unmounting it. The notice therefore lives here, and is rendered by
   * a component that is not conditional on `isActive`.
   */
  const [completionNotice, setCompletionNotice] = useState<{
    flow: OnboardingExperience;
    label: string;
  } | null>(null);

  /**
   * Whether the floating help button has been hidden for this session.
   *
   * "Hide Help Button" called `closeHelpMenu`, which set `showHelpMenu` —
   * a value nothing rendered from. `GlobalTutorialBar` shows the help
   * affordance from `isHelpMode`, so the button did not control its own
   * visibility and pressing it did nothing at all.
   *
   * Session-only and deliberately NOT persisted: hiding a button is not the
   * same as finishing or dismissing onboarding, and must not become durable
   * state that quietly reads like either. `completed` stays true, `dismissed`
   * stays false, and the account-menu walkthrough entry is untouched.
   */
  const [helpButtonHidden, setHelpButtonHidden] = useState(false);
  
  // Debounce timer for saving to Supabase
  /**
   * CORE-BL: why no experience is showing, when none is.
   *
   * Held separately from `state` because "we could not work out your role"
   * and "you have not started" are different things to say, and the old code
   * could say neither.
   */
  const [rawMultiRoleExperiences, setMultiRoleExperiences] =
    useState<MultiRoleExperiences | null>(null);
  const [rawExperienceResolution, setExperienceResolution] =
    useState<ExperienceResolution | null>(null);
  /**
   * The canonical role behind the active experience.
   *
   * Held separately from the resolution so the step sequence can apply role
   * gates even after the resolution has been consumed.
   */
  const [rawResolvedRole, setResolvedRole] = useState<KnownAppRole | null>(null);
  /** Core-100 R4: the person's work roles behind the active experience (route reachability is the union). */
  const [rawResolvedWorkRoles, setResolvedWorkRoles] = useState<ReadonlyArray<string> | null>(null);
  /** Core-100 R4: an offered experience the person chose to switch to (per user+shop scope). */
  // A ref, not state: it is read by `loadState`, whose dependencies stay the ID-scoped
  // trigger set (ARC-A3); `switchExperience` re-runs the load itself.
  const chosenExperienceRef = useRef<{ scope: string; experience: OnboardingExperience } | null>(null);
  const permissions = usePermissions();
  /**
   * Whether the person's stored progress could be established.
   *
   * `'failed'` is not `'loaded'` with nothing in it. The difference decides
   * whether it is safe to write.
   */
  const [rawProgressRead, setProgressRead] = useState<
    'loading' | 'loaded' | 'failed'
  >('loading');

  /**
   * Which auth scope the resolution, role and progress-read status describe.
   *
   * Set when a load publishes; compared against the CURRENT scope on every
   * render.
   */
  const [resolutionScope, setResolutionScope] = useState<string | null>(null);

  /**
   * Whether in-memory progress has reached the database.
   *
   *   idle     nothing to save yet
   *   pending  changed locally, still inside the debounce or in flight
   *   saved    the write landed
   *   error    the write failed; the progress is real and NOT durable
   *
   * `pending` matters: without it a change made 200ms ago still read as
   * "saved", which is a durability claim the write had not yet earned.
   */
  /**
   * Durability status PER ROW, not one last-write-wins slot.
   *
   * A single `{scope, status}` slot stopped an old shop's result from
   * CLAIMING the current shop was saved, which was the point. But it created
   * the mirror problem: a slow write for shop A completing after the person
   * had moved to shop B replaced the slot with A's entry, and B's own
   * `pending`/`saved`/`error` silently became `idle`. B's durability truth
   * was erased by a write that had nothing to do with B.
   *
   * Keyed by row, each row's outcome is its own. A late A result updates A's
   * entry and leaves B's alone.
   */
  const [saveReports, setSaveReports] = useState<Record<string, SaveStatus>>(
    {},
  );

  const reportSave = useCallback((key: string, status: SaveStatus) => {
    setSaveReports((prev) =>
      prev[key] === status ? prev : { ...prev, [key]: status },
    );
  }, []);

  /**
   * THE step sequence for the person currently in an experience.
   *
   * Everything below reads this: current step, next, previous, jump, skip,
   * gating, progress and completion. It used to be `getStepsForFlow(flow)`
   * at four separate call sites, which returned the RAW track — so a
   * dispatcher offered a filtered front-of-house walkthrough was then walked
   * through the intake steps that had been filtered out of it. The menu and
   * the runtime were two step authorities that agreed only by luck.
   */
  /**
   * The permission surface, held by reference and compared by VALUE.
   *
   * `usePermissions()` builds a fresh object on every render, so anything
   * memoised on it is memoised on nothing: `activeSteps` produced a new array
   * each render, every callback listing it rebuilt, and every effect
   * depending on those callbacks re-ran. The first-use coordinator's start
   * timer was cleared and rescheduled on each of those re-runs and could
   * never actually fire.
   *
   * The signature is the boolean capability members only, sorted — the exact
   * inputs `applicableSteps` reads. It is recomputed each render (cheap) but
   * its VALUE only changes when a capability genuinely changes, which is what
   * the memo below needs.
   */
  const permissionsRef = useRef(permissions);
  permissionsRef.current = permissions;
  const capabilitySignature = useMemo(
    () =>
      Object.entries(permissions ?? {})
        .filter(([, value]) => typeof value === 'boolean')
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([key, value]) => `${key}:${value ? 1 : 0}`)
        .join('|'),
    [permissions],
  );

  /**
   * The scope a resolution belongs to: person, shop, AND role.
   *
   * The role is part of it deliberately. A role change inside the SAME shop
   * leaves `user` and `activeShop` identical, so a scope built from those two
   * alone cannot tell that a technician has just become a service advisor —
   * and the technician row still matches, so row-identity masking cannot
   * either. Their old walkthrough would have stayed writable while the app
   * already knew the role had changed.
   *
   * `permissions.role` is the reactive signal, not the authority: the
   * membership read inside `loadState` remains the authority for what a
   * person is taught. This only decides WHEN the previous answer stops being
   * treated as the current one.
   */
  const authScopeKey =
    user?.id && activeShop?.id
      ? `${user.id}::${activeShop.id}::${permissions.role ?? 'none'}`
      : null;

  /**
   * Whether the resolution in memory describes the scope signed in NOW.
   *
   * Pass 3 masked the stored ROW synchronously during render, which closed
   * the "shop A's tutorial presented as shop B's" hole. It did not cover
   * `experienceResolution`, `resolvedRole` or `progressRead`: those are not
   * row-derived, and were cleared only when the load EFFECT ran. An effect
   * runs a frame late, so between `activeShop` becoming B and the load
   * starting there was a render in which the row was correctly masked and the
   * resolution still described A — enough for `startResolvedTutorial` to
   * build `user + shop B + A's experience` and write it.
   *
   * With no signed-in scope there is nothing to resolve, so the question does
   * not arise and the answer is vacuously yes.
   */
  const resolutionIsCurrent =
    authScopeKey === null ? true : resolutionScope === authScopeKey;

  /**
   * The resolution, role and read status — masked to the current scope.
   *
   * A stale resolution is not a resolution for the new scope. It reads as
   * "not established yet", which is the truth, and is what every consumer
   * already knows how to wait on.
   */
  const experienceResolution = resolutionIsCurrent
    ? rawExperienceResolution
    : null;

  // Masked on exactly the same condition. A set of walkthroughs belonging to
  // shop A offered while shop B is on screen is the same defect the single
  // resolution already guards against, one cardinality wider.
  const multiRoleExperiences = resolutionIsCurrent
    ? rawMultiRoleExperiences
    : null;

  /**
   * Loading covers the gap the effect leaves.
   *
   * `setIsLoading(true)` at the top of `loadState` is a frame late, and every
   * consumer that treats `isLoading === false` as "the answer is in" would
   * spend that frame acting on the previous scope's answer. A resolution that
   * does not belong to the current scope IS a load in progress, whether or
   * not the effect has run yet.
   */
  const isLoading = rawIsLoading || !resolutionIsCurrent;
  const resolvedRole = resolutionIsCurrent ? rawResolvedRole : null;
  const resolvedWorkRoles = resolutionIsCurrent ? rawResolvedWorkRoles : null;
  const progressRead: 'loading' | 'loaded' | 'failed' = resolutionIsCurrent
    ? rawProgressRead
    : 'loading';

  /**
   * The row's OWN identity.
   *
   * A stored row already carries the person, shop and contract it belongs to.
   * Reading its scope from anywhere else is how a synthetic identity gets
   * built — see `currentScope` below.
   */
  const stateScope = useMemo<SaveScope | null>(() => {
    if (!rawState?.user_id || !rawState?.shop_id || !rawState?.flow) {
      return null;
    }
    return {
      userId: rawState.user_id,
      shopId: rawState.shop_id,
      flow: rawState.flow,
    };
  }, [rawState?.user_id, rawState?.shop_id, rawState?.flow]);

  /**
   * Whether the row in memory belongs to the person and shop signed in NOW.
   *
   * Switching shops is asynchronous: `activeShop.id` becomes B immediately,
   * and B's onboarding read finishes some time later. In between, the row in
   * memory is still A's.
   *
   * Two things went wrong in that window. The scope was previously built as
   * `user + activeShop.id + state.flow`, which during the transition composes
   * the NEW shop with the OLD row's contract — an identity that never
   * produced the state being edited, and one a write could have been sent to.
   * And every public value below still described A's tutorial while the
   * person was looking at B, so they could press Next on it.
   *
   * This is computed during render rather than fixed in an effect, because an
   * effect runs a frame late — and one frame is enough to build the hybrid.
   */
  const stateIsCurrentForAuthScope =
    !!stateScope &&
    !!user?.id &&
    !!activeShop?.id &&
    stateScope.userId === user.id &&
    stateScope.shopId === activeShop.id &&
    // …and the resolution behind it still belongs to this scope. Without
    // this a same-shop role change left the old row fully interactive: a
    // technician promoted to advisor could keep pressing Next on the
    // technician walkthrough, writing technician progress, while the app was
    // already re-reading their membership.
    resolutionIsCurrent;

  /**
   * The row this person is actually looking at.
   *
   * Everything below reads THIS, never `rawState`. A row from another shop is
   * masked to `null` — it is not lost (its queued writes carry their own
   * immutable scope and still land), it simply stops being presented as this
   * shop's tutorial.
   */
  const state = stateIsCurrentForAuthScope ? rawState : null;

  /**
   * The row a write may be made against — the row's own scope, and only while
   * it is still the signed-in one.
   */
  const currentScope: SaveScope | null = stateIsCurrentForAuthScope
    ? stateScope
    : null;

  const currentScopeKey = scopeKey(currentScope);

  const activeSteps = useMemo<OnboardingStep[]>(() => {
    if (!state?.flow) return [];
    return stepsForActiveExperience(
      state.flow,
      resolvedRole,
      permissionsRef.current,
      resolvedWorkRoles,
    );
    // `capabilitySignature` stands in for `permissions`: same information,
    // stable identity. The lint rule cannot see through the ref, and listing
    // `permissions` itself would restore the per-render churn this exists to
    // remove — a churn that stopped the first-use start timer from ever
    // firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.flow, resolvedRole, resolvedWorkRoles, capabilitySignature]);

  /**
   * A save status only describes the row it was recorded for.
   *
   * If no status has been recorded for the row on screen, the honest answer
   * is `idle` — we are not reporting on this row, rather than reporting some
   * other row's outcome as if it were this one's.
   */
  const saveStatus: SaveStatus =
    (currentScopeKey ? saveReports[currentScopeKey] : undefined) ?? 'idle';

  /**
   * Which load is the current one.
   *
   * Two reads of different shops can be open at once, and the network decides
   * which returns first. Shop A's read finishing after shop B's would publish
   * A's membership, A's role, A's progress and A's row while the person is
   * looking at B — the classic out-of-order overwrite, and nothing stopped
   * it.
   *
   * Every load takes a ticket. A load may FINISH whenever it likes; it may
   * only PUBLISH while its ticket is still the current one.
   */
  const loadGenerationRef = useRef(0);

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * Pending write, WITH the row it belongs to.
   *
   * Previously a bare `Partial<OnboardingState>`: the payload was assembled
   * at flush time from whatever `user`, `activeShop` and `state.flow` then
   * held. Two ways that went wrong. Switching shops inside the debounce wrote
   * shop A's step number into shop B's row. And merging a second update into
   * the queue merged ACROSS scopes — a queued shop A change and a new shop B
   * change became one payload with one flow.
   */
  const saveQueueRef = useRef<{
    scope: SaveScope;
    updates: Partial<OnboardingState>;
  } | null>(null);

  /**
   * Load onboarding state from Supabase
   */
  const loadState = useCallback(async () => {
    // Take a ticket. Anything already in flight is now stale and will be
    // refused publication when it returns.
    const generation = ++loadGenerationRef.current;
    const isCurrent = () => loadGenerationRef.current === generation;
    // The scope this load is FOR, captured now. Everything it publishes is
    // stamped with it, so a later render can tell whether the answers still
    // describe the person and shop on screen.
    const scopeAtStart =
      user?.id && activeShop?.id
        ? `${user.id}::${activeShop.id}::${permissions.role ?? 'none'}`
        : null;

    // Clear the previous scope's answers IMMEDIATELY, before awaiting
    // anything. The row itself is masked during render (see
    // `stateIsCurrentForAuthScope`), but the resolution, the role and the
    // read status are not row-derived — left standing they would describe
    // the previous shop for the whole of the new shop's loading window.
    setExperienceResolution(null);
    setMultiRoleExperiences(null);
    setResolvedRole(null);
    setResolvedWorkRoles(null);
    setResolutionScope(null);
    setState(null);
    setProgressRead('loading');
    setIsLoading(true);

    if (!user || !activeShop) {
      setIsLoading(false);
      return;
    }

    try {
      // CORE-BL: read the membership, and do NOT invent a role from it.
      //
      // `.maybeSingle()` stays — it correctly avoids the PGRST116 throw that
      // used to leave `isLoading` stuck (ONB-R69-FIX (MED-1)). What changes is
      // the interpretation. The previous line was:
      //
      //     const flow = membership?.role === 'MANAGER' ? 'manager' : 'technician';
      //
      // and its own comment described falling back to the technician flow as
      // "graceful". It was not: technician is a real job, so assigning it to
      // somebody whose membership merely had not loaded is a claim nobody
      // made — and it happened to select the one library that was empty.
      //
      // A failed read, an absent membership and an unfamiliar role are now
      // three distinct states, and none of them becomes a role.
      const { data: membership, error: membershipError } = await supabase
        // `authority_role` and `membership_work_roles` are newer than the
        // checked-in generated types, which are produced from the hosted
        // schema. The repository's established convention for that gap is a
        // narrow `as never` at the query boundary — the same one
        // `TeamManagement` uses — rather than weakening the row types
        // everywhere or regenerating against an unmigrated environment.
        .from('memberships')
        .select('*')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .eq('status', 'active')
        .maybeSingle();

      // WHAT THIS PERSON DOES, which is no longer the same question as what
      // they ARE. Andy writes repair orders in the morning and turns wrenches
      // in the afternoon; the scalar could only ever teach him one of those.
      //
      // A failed read is NOT an empty set. `null` means "we do not know" and
      // falls back to the scalar resolution, exactly as it would for a
      // membership written before multi-role existed. Treating a network
      // failure as "performs no work" would silently withdraw somebody's
      // walkthrough.
      let workRoles: WorkRole[] | null = null;
      // `authority_role` is newer than the checked-in generated types, which
      // are produced from the hosted schema. The repository's convention for
      // that gap — the one `TeamManagement` already uses — is to read the
      // column off the row through an explicit cast rather than weakening the
      // row type everywhere or regenerating against an unmigrated schema.
      const membershipRow = membership as unknown as
        | { id: string; role: string | null; authority_role: string | null }
        | null;

      if (membershipRow?.id) {
        const { data: roleRows, error: roleError } = await supabase
          .from('membership_work_roles' as never)
          .select('work_role')
          .eq('membership_id', membershipRow.id);
        if (!roleError) {
          workRoles = ((roleRows ?? []) as { work_role: string }[])
            .map((r) => r.work_role as WorkRole);
        }
      }

      const membershipResolution = readMembership({
        isLoading: false,
        errored: !!membershipError,
        role: membershipRow?.role ?? null,
      });

      const legacyResolution = resolveOnboardingExperience(membershipResolution);

      const multiRole = resolveMultiRoleExperiences({
        membership: membershipResolution,
        authority: (membershipRow?.authority_role ?? null) as AuthorityRole | null,
        workRoles,
      });

      // Core-100 R4 CORE-BL (OD-14): the walkthrough that RUNS is chosen from the
      // person's offered set, not the legacy scalar alone. A pure Detail
      // Specialist is STAFF on the scalar and used to be put on the read-only
      // track (whose pages STAFF cannot open — so, in practice, nothing). The
      // legacy experience stays in force whenever it is offered, so nobody's
      // existing progress changes meaning; a switch the person made is honoured
      // only while it is still one of theirs.
      const scopeForChoice = `${user.id}::${activeShop.id}`;
      const chosenExperience = chosenExperienceRef.current;
      const switched =
        chosenExperience && chosenExperience.scope === scopeForChoice &&
        multiRole.experiences.includes(chosenExperience.experience)
          ? chosenExperience.experience
          : null;
      const activeExperience = switched ?? chooseActiveExperience(multiRole);
      const resolution: ExperienceResolution =
        legacyResolution.status === 'experience' && activeExperience
          ? {
              ...legacyResolution,
              experience: activeExperience,
              ...(workRoles != null ? { workRoles } : {}),
            }
          : legacyResolution;

      // A stale read may finish. It may not publish.
      if (!isCurrent()) return;

      setResolutionScope(scopeAtStart);
      setExperienceResolution(resolution);
      setMultiRoleExperiences(multiRole);
      setResolvedWorkRoles(workRoles);
      setResolvedRole(
        resolution.status === 'experience' ? resolution.role : null,
      );

      if (resolution.status !== 'experience') {
        // Nothing to resume, and nothing to fabricate. The app is not
        // blocked; the surfaces render the reason instead of a tour.
        setState(null);
        return;
      }

      const flow = resolution.experience;

      // Load existing progress.
      //
      // The error was previously discarded, so a failed read looked exactly
      // like "no row" — and "you have not started" is a claim. Worse, a
      // subsequent start would upsert over progress the app could not see,
      // restarting somebody's tutorial because the database had a bad
      // moment.
      const { data: existing, error: progressError } = await supabase
        .from('onboarding_state')
        .select('*')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .eq('flow', flow)
        .maybeSingle();

      if (!isCurrent()) return;

      if (progressError) {
        setProgressRead('failed');
        setState(null);
        console.error('Failed to read onboarding progress:', progressError);
        return;
      }

      setProgressRead('loaded');
      if (existing) {
        setState(existing as OnboardingState);
      } else {
        // A successful read that found nothing. This one IS "not started".
        setState(null);
      }
    } catch (error) {
      console.error('Failed to load onboarding state:', error);
    } finally {
      // A stale load must not lift the CURRENT load's spinner either —
      // reporting "loaded" while the real read is still open is how a
      // consumer concludes "no tutorial" and acts on it.
      if (isCurrent()) setIsLoading(false);
    }
    // ARC-A3: deps tightened from `[user, activeShop]` (full objects) to
    // `[user?.id, activeShop?.id]`. loadState reads `user.id` and
    // `activeShop.id` exclusively; ID-only is sufficient and prevents
    // the callback from rebuilding on every activeShop reference
    // replacement (which previously cascaded into the `[loadState]`
    // effect at L439, re-firing the whole load on unrelated AuthContext
    // ripples).
    //
    // CORE-BL adds `permissions.role`, and it is a TRIGGER, not an authority.
    // The membership read below remains the authority for which experience a
    // person gets; this dependency only decides WHEN that read re-runs.
    //
    // Without it, a role change inside the same shop was invisible. A
    // technician promoted to service advisor mid-session kept
    // `technician_core_v1` — its resolved role, its capability filters and
    // its route filters — until they reloaded the page. They would have been
    // walked through technician lessons in an advisor's job, and their
    // progress would have been written to the technician contract. Shop
    // switches already reloaded; role changes did not, and a promotion is
    // exactly the moment somebody needs teaching.
  }, [user?.id, activeShop?.id, permissions.role]);

  const switchExperience = useCallback(
    (experience: OnboardingExperience): boolean => {
      if (!user || !activeShop) return false;
      if (!multiRoleExperiences || !multiRoleExperiences.experiences.includes(experience)) return false;
      chosenExperienceRef.current = { scope: `${user.id}::${activeShop.id}`, experience };
      void loadState();
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, activeShop?.id, multiRoleExperiences, loadState],
  );

  /**
   * Debounced save to Supabase
   *
   * ONB-R69-FIX (CRIT-1): previously this early-returned on `!state?.flow`
   * and read `state.flow` from the closure when building the payload.
   * On the *first* `startTutorial()` call, the caller did
   * `setState(newState)` and then immediately `await saveToSupabase(newState)`
   * — but because React batches state updates, `state` in this closure was
   * still `null`, so the early return fired and the insert never ran.
   * The in-memory tutorial would look active to the user, but on refresh
   * `loadState` returned null (nothing persisted) and the tutorial silently
   * vanished. Resolve flow from updates first (which is what the caller
   * already passes on the initial call) before falling back to closure
   * state, and keep the early return defensive for the (impossible-today)
   * case where neither source has a flow.
   */
  const saveToSupabase = useCallback(
    async (
      scope: SaveScope,
      updates: Partial<OnboardingState>,
    ): Promise<boolean> => {
      const key = scopeKey(scope);
      // In flight is not idle. `startTutorial` writes directly rather than
      // through the debounce, so without this the first save reported
      // `idle` for the whole time the upsert was open — a window in which
      // the tutorial is on screen and its durability is genuinely unknown.
      reportSave(key, 'pending');
      try {
        // The identity comes from the SCOPE, and the caller's updates cannot
        // move it. Spreading `updates` last used to mean a stale queued
        // `flow` could redirect the write into a different row from the one
        // whose status was being reported.
        const payload = {
          ...updates,
          user_id: scope.userId,
          shop_id: scope.shopId,
          flow: scope.flow,
        };

        const { error } = await supabase
          .from('onboarding_state')
          .upsert(payload, {
            onConflict: 'user_id,shop_id,flow',
          });

        if (error) throw error;
        // CORE-BL: only a save that actually landed counts as saved, and it
        // counts only for the row it landed in. The scope is recorded next to
        // the status; the exposed `saveStatus` shows it only while that row
        // is the one on screen.
        reportSave(key, 'saved');
        return true;
      } catch (error) {
        console.error('Failed to save onboarding state:', error);
        // The progress is real in memory and is NOT durable. Surfaces that
        // reassure somebody their place is remembered must be able to tell
        // the difference — a swallowed write that still reads as "saved" is
        // how a person loses their position and is told nothing.
        reportSave(key, 'error');
        return false;
      }
      // ARC-A3 §G, superseded and recorded rather than dropped. This used to
      // depend on `[user?.id, activeShop?.id, state?.flow]`, which was the
      // ID-scoped tightening ARC-A3 asked for. CORE-BL closure pass 2 removed
      // those deps entirely — not to loosen the doctrine but to go further
      // than it: the write must not READ any of them. Everything it writes
      // with arrives in `scope`, captured when the write was queued.
      //
      // The only dependency is the reporter. Everything this writes with
      // arrives in `scope` — it used to close over `user`, `activeShop` and
      // `state?.flow`, which is what allowed a debounced write to land in
      // whichever row happened to be current when the timer fired.
    },
    [reportSave],
  );

  /**
   * Queue state update with debounced save
   */
  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    // The row this change belongs to, fixed NOW. Nothing downstream may
    // re-derive it.
    const scope = currentScope;
    if (!scope) {
      // No signed-in person, shop or flow: there is no row to write into, so
      // queueing would only produce a payload with no identity.
      return;
    }
    const key = scopeKey(scope);

    setState(prev => (prev ? { ...prev, ...updates } : null));
    // Changed locally and not yet written. Reporting this as saved was a
    // promise the debounce had not kept.
    reportSave(key, 'pending');

    const queued = saveQueueRef.current;
    if (queued && scopeKey(queued.scope) !== key) {
      // The pending write belongs to a DIFFERENT row. It is not merged into
      // this one and it is not dropped — dropping it would silently discard a
      // change the person already made, and merging would write it into a row
      // it was never about. It is flushed as its own write.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      void saveToSupabase(queued.scope, queued.updates);
      saveQueueRef.current = null;
    }

    // Queue for save, merging only within the same row.
    saveQueueRef.current = {
      scope,
      updates: { ...(saveQueueRef.current?.updates ?? {}), ...updates },
    };

    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer (500ms debounce)
    saveTimerRef.current = setTimeout(() => {
      const pending = saveQueueRef.current;
      if (pending) {
        saveQueueRef.current = null;
        void saveToSupabase(pending.scope, pending.updates);
      }
    }, 500);
  }, [saveToSupabase, currentScope, reportSave]);

  /**
   * Start tutorial
   */
  /**
   * Begin the experience THIS PERSON resolves to. No caller chooses it.
   *
   * The previous signature took a flow, and checked only that the person had
   * *some* resolved experience — not that the flow they were being started on
   * was it. So a technician could be started on `manager`, the manager
   * library would be route-filtered down to whatever a technician can open,
   * and that shortened sequence would be persisted under the durable
   * `manager` key. CORE-BL's whole identity rule is ONE KEY = ONE EXACT
   * ORDERED SEQUENCE; a caller-supplied key breaks it directly, and it breaks
   * it in the database rather than on screen.
   */
  const startResolvedTutorial =
    useCallback(async (): Promise<StartTutorialResult> => {
      // Every refusal below is returned, not swallowed. A caller that
      // navigates to the first lesson, or spends a one-shot first-use
      // invitation, is acting on the assumption that a tutorial now exists —
      // and until this returned a result, a refusal and a start looked
      // identical to it.
      if (!user || !activeShop) {
        return { status: 'refused', reason: 'no_session' };
      }

      // Do not start for somebody whose role we have not established.
      if (
        !experienceResolution ||
        experienceResolution.status !== 'experience'
      ) {
        return { status: 'refused', reason: 'experience_unknown' };
      }

      // Refuse to start over progress we could not read. An upsert here would
      // reset a tutorial somebody is halfway through, because the database
      // failed for a moment and the app assumed absence.
      if (progressRead === 'failed') {
        console.warn(
          '[CORE-BL] Not starting: existing onboarding progress could not be read, and starting would overwrite it.',
        );
        return { status: 'refused', reason: 'progress_failed' };
      }
      if (progressRead !== 'loaded') {
        // Still reading. Absence has not been established yet, so writing
        // `current_step: 0` would be writing over an unknown.
        return { status: 'refused', reason: 'progress_unread' };
      }

      // Ask the canonical resolver, and carry its answer through unchanged.
      //
      // This used to be a second zero-length check here, which collapsed
      // `route_blocked` into `no_content` — so a dispatcher, whose lessons
      // exist and whose routes refuse them, was told nothing was written for
      // their role. The resolver already draws that distinction; reducing it
      // at the boundary threw away the only accurate thing anyone could say.
      const resolved = resolveExperienceSteps(
        experienceResolution,
        permissionsRef.current,
      );
      if (resolved.status !== 'steps') {
        return {
          status: 'refused',
          reason: resolved.reason === 'route_blocked'
            ? 'route_blocked'
            : 'no_content',
          message: resolved.message,
        };
      }

      const flow = resolved.experience;
      breadcrumbs.log('OB:GUIDE_START', { flow, shopId: activeShop.id });

      const newState: OnboardingState = {
        user_id: user.id,
        shop_id: activeShop.id,
        flow,
        current_step: 0,
        completed: false,
        dismissed: false,
        steps_completed: [],
        started_at: clock.now().toISOString(),
      };

      setState(newState);
      const saved = await saveToSupabase(
        { userId: user.id, shopId: activeShop.id, flow },
        newState,
      );
      if (!saved) {
        // The tutorial is running in memory and did not reach the database.
        // Reporting that plainly lets the caller decline to promise it will
        // still be there after a refresh; `saveStatus` is already `error`.
        return { status: 'refused', reason: 'save_failed' };
      }
      return { status: 'started', flow };
      // ARC-A3 §G: the ID-scoped deps this replaced `startTutorial` with are
      // kept — `user?.id` and `activeShop?.id`, never the full objects. The
      // additions are what the start now genuinely reads: the progress-read
      // status it refuses on, the resolution it starts FROM, and a
      // value-compared capability signature.
    }, [
      user?.id,
      activeShop?.id,
      saveToSupabase,
      progressRead,
      experienceResolution,
      // Value-compared, so this keeps its identity across renders in which
      // nothing about the person's capabilities changed.
      capabilitySignature,
    ]);

  /**
   * Check if current step's gate is satisfied
   */
  const checkCurrentGate = useCallback(async (): Promise<boolean> => {
    if (!state || !activeShop || !user) return false;

    const steps = activeSteps;
    const currentStepData = steps[state.current_step];
    if (!currentStepData) return false;

    const ctx: GateCheckContext = {
      shopId: activeShop.id,
      userId: user.id,
    };

    return checkStepGate(currentStepData.id, ctx);
    // ARC-A3: deps tightened from `[state, activeShop, user]` to
    // `[state, activeShop?.id, user?.id]`. checkCurrentGate reads
    // `state` (full — needs flow + current_step + steps_completed),
    // `activeShop.id`, and `user.id` only. State stays as full object
    // since multiple fields are read; user/activeShop are id-scoped.
  }, [state, activeShop?.id, user?.id, activeSteps]);

  /**
   * Mark specific step as complete
   */
  const markStepComplete = useCallback(async (stepId: string) => {
    if (!state) return;

    const stepsCompleted = [...state.steps_completed];
    if (!stepsCompleted.includes(stepId)) {
      stepsCompleted.push(stepId);
      updateState({ steps_completed: stepsCompleted });
    }
  }, [state, updateState]);

  /**
   * Advance to next step
   */
  const nextStep = useCallback(async () => {
    if (!state) return;

    const steps = activeSteps;
    const currentStepData = steps[state.current_step];
    
    // Log step completion
    breadcrumbs.log('OB:GUIDE_STEP_OK', {
      flow: state.flow,
      stepId: currentStepData?.id,
      stepIndex: state.current_step,
    });

    // Mark current step complete
    if (currentStepData) {
      await markStepComplete(currentStepData.id);
    }

    const nextStepIndex = state.current_step + 1;
    
    if (nextStepIndex >= steps.length) {
      // Tutorial complete
      breadcrumbs.log('OB:GUIDE_DONE', {
        flow: state.flow,
        totalSteps: steps.length,
      });
      
      updateState({
        completed: true,
        current_step: steps.length - 1,
      });
    } else {
      updateState({
        current_step: nextStepIndex,
      });
    }
  }, [state, markStepComplete, updateState, activeSteps]);

  /**
   * Go back to previous step
   */
  /**
   * Finish the walkthrough this person is on — one truthful operation.
   *
   * The final Coachmark button used to do `setShowCompletionDialog(true)` and
   * nothing else. The dialog's two exits were "Maybe Later", which called
   * `dismissTutorial` (dismissed true, completed FALSE), and "Start User
   * Tutorial", which started a different flow entirely. So a technician who
   * walked every step of `technician_core_v1` and pressed Complete ended with
   * a row that recorded no completion at all — and CORE-BL had just spent a
   * pass making completed and dismissed mean different things.
   *
   * This marks the last step, sets `completed`, leaves `dismissed` alone, and
   * writes against the flow already on screen. It manufactures no other flow.
   */
  const completeTutorial = useCallback(async () => {
    if (!state) return;

    const steps = activeSteps;
    const finalStep = steps[state.current_step];
    const stepsCompleted = [...state.steps_completed];
    if (finalStep && !stepsCompleted.includes(finalStep.id)) {
      stepsCompleted.push(finalStep.id);
    }

    breadcrumbs.log('OB:GUIDE_DONE', {
      flow: state.flow,
      totalSteps: steps.length,
    });

    // Raise the notice HERE, where it survives the coachmark that is about to
    // unmount because `completed` makes `isActive` false.
    setCompletionNotice({
      flow: state.flow,
      label: EXPERIENCE_LABEL[state.flow],
    });

    updateState({
      steps_completed: stepsCompleted,
      completed: true,
      // Explicit. Finishing is not hiding, and the two were being conflated
      // by the only path a person could actually take to the end.
      dismissed: false,
      current_step: Math.max(0, steps.length - 1),
    });
  }, [state, updateState, activeSteps]);

  const previousStep = useCallback(async () => {
    if (!state) return;

    const prevStepIndex = state.current_step - 1;
    
    if (prevStepIndex < 0) {
      return; // Already at first step
    }

    breadcrumbs.log('OB:GUIDE_PREV', {
      flow: state.flow,
      stepIndex: prevStepIndex,
    });

    updateState({
      current_step: prevStepIndex,
      completed: false, // Un-complete if we go back
    });
  }, [state, updateState]);

  /**
   * Jump to a specific step
   */
  const jumpToStep = useCallback(async (stepIndex: number) => {
    if (!state) return;

    const steps = activeSteps;
    
    if (stepIndex < 0 || stepIndex >= steps.length) {
      return; // Invalid step index
    }

    updateState({
      current_step: stepIndex,
      completed: false,
    });
  }, [state, updateState, activeSteps]);

  /**
   * Skip current step
   */
  const skipStep = useCallback(async () => {
    if (!state) return;

    const steps = activeSteps;
    const currentStepData = steps[state.current_step];
    
    breadcrumbs.log('OB:GUIDE_SKIP', {
      flow: state.flow,
      stepId: currentStepData?.id,
      stepIndex: state.current_step,
    });

    await nextStep(); // Same as next, just with different telemetry
  }, [state, nextStep, activeSteps]);

  /**
   * Dismiss tutorial - completely closes and ends the tutorial
   */
  const dismissTutorial = useCallback(async () => {
    if (!state) return;

    breadcrumbs.log('OB:GUIDE_DISMISS', {
      flow: state.flow,
      atStep: state.current_step,
    });

    // CORE-BL: dismissal is NOT completion.
    //
    // This wrote `{ dismissed: true, completed: true }`, so "not now" was
    // recorded as "finished". The constitution treats completed, dismissed,
    // resumed and replayed as four different things, and it relies on the
    // difference: a person who declined instruction and a person who
    // received it look identical once one is written as the other, and the
    // product then cannot tell how many people it failed to teach.
    //
    // `completed` is left exactly as it was. Somebody who genuinely finished
    // and later dismissed the presentation keeps their completion; somebody
    // who declined at step two does not acquire one.
    updateState({ dismissed: true });
  }, [state, updateState]);

  /**
   * Resume tutorial
   */
  const acknowledgeCompletion = useCallback(() => {
    setCompletionNotice(null);
  }, []);

  const hideHelpButton = useCallback(() => {
    setHelpButtonHidden(true);
    setShowHelpMenu(false);
  }, []);

  const resumeTutorial = useCallback(() => {
    if (!state) return;

    breadcrumbs.log('OB:GUIDE_RESUME', {
      flow: state.flow,
      resumeStep: state.current_step,
    });

    updateState({ dismissed: false });
  }, [state, updateState]);

  /**
   * Replay tutorial from start
   */
  const replayTutorial =
    useCallback(async (): Promise<StartTutorialResult> => {
      // Replays THIS PERSON'S experience. It used to take a flow, and the
      // help menu passed `manager` or `user` to whoever happened to have
      // finished something — so a technician who completed
      // `technician_core_v1` was offered two walkthroughs that were not
      // theirs, and taking either wrote a durable row under a contract they
      // had never been resolved to.
      if (
        !experienceResolution ||
        experienceResolution.status !== 'experience'
      ) {
        return { status: 'refused', reason: 'experience_unknown' };
      }

      const target = experienceResolution.experience;

      // Already on that contract: reset it in place, so the row keeps its
      // identity and its history rather than being replaced.
      if (state && state.flow === target) {
        breadcrumbs.log('OB:GUIDE_START', { flow: target, replay: true });
        updateState({
          current_step: 0,
          completed: false,
          dismissed: false,
          steps_completed: [],
        });
        setShowHelpMenu(false);
        return { status: 'started', flow: target };
      }

      // Otherwise start the resolved experience — including when the row in
      // memory is a LEGACY contract (`user`, `technician`, `setup_wizard`).
      // That old row is left exactly as it is; a replay must not reset a
      // historical record, and it must not teach somebody a contract that is
      // not theirs. The refusal is passed straight through, because a replay
      // that could not start is not a replay and closing the menu on it would
      // look exactly like success.
      const result = await startResolvedTutorial();
      if (result.status === 'started') {
        breadcrumbs.log('OB:GUIDE_START', { flow: target, replay: true });
        setShowHelpMenu(false);
      }
      return result;
    }, [state, updateState, startResolvedTutorial, experienceResolution]);

  /**
   * Toggle minimize state
   */
  const toggleMinimize = useCallback(() => {
    const newValue = !isMinimized;
    setIsMinimized(newValue);
    localStorage.setItem('tutorial-minimized', String(newValue));
  }, [isMinimized]);

  /**
   * Open help menu
   */
  const openHelpMenu = useCallback(() => {
    setShowHelpMenu(true);
  }, []);

  /**
   * Close help menu and dismiss help mode
   */
  const closeHelpMenu = useCallback(() => {
    // CORE-BL: closing the help menu closes the help menu.
    //
    // It used to also write `dismissed: true`, so somebody who opened the
    // reference, read one entry and closed it had silently declined their
    // onboarding — an outcome they never chose and were never shown. The two
    // actions are separate: `dismissTutorial()` is how a person declines the
    // walkthrough, and it is reached from a control that says so.
    setShowHelpMenu(false);
  }, []);

  // Load state on mount
  useEffect(() => {
    loadState();
  }, [loadState]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  // Derive current step data
  /**
   * The experience this person resolves to — the only thing CORE-BL may
   * start, replay or name for them.
   */
  const resolvedExperience: OnboardingExperience | null =
    experienceResolution?.status === 'experience'
      ? experienceResolution.experience
      : null;

  /**
   * The single offer. Computed from the SAME resolution the start uses, so
   * the menu's destination and the started walkthrough cannot disagree.
   */
  const offered = useMemo(() => {
    if (!experienceResolution || experienceResolution.status !== 'experience') {
      return {
        offer: null as OnboardingOffer | null,
        reason:
          experienceResolution && experienceResolution.status === 'unresolved'
            ? experienceResolution.reason
            : null,
      };
    }
    const resolved = resolveExperienceSteps(
      experienceResolution,
      permissionsRef.current,
    );
    if (resolved.status !== 'steps') {
      return { offer: null as OnboardingOffer | null, reason: resolved.reason };
    }
    return {
      offer: {
        experience: resolved.experience,
        label: EXPERIENCE_LABEL[resolved.experience],
        firstStepPage: resolved.steps[0].page,
        stepCount: resolved.steps.length,
      } as OnboardingOffer,
      reason: null as UnresolvedReason | null,
    };
    // `capabilitySignature` stands in for `permissions`; see `activeSteps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceResolution, capabilitySignature]);

  const onboardingOffer = offered.offer;
  const offerUnavailableReason = offered.reason;

  /**
   * One label authority. The row on screen names itself; otherwise the
   * experience they would be offered does.
   */
  const experienceLabel: string | null = state?.flow
    ? EXPERIENCE_LABEL[state.flow]
    : resolvedExperience
      ? EXPERIENCE_LABEL[resolvedExperience]
      : null;

  const currentStepData = state
    ? activeSteps[state.current_step] || null
    : null;

  // Help mode: completed but not permanently dismissed
  const isHelpMode = !!(state?.completed && !state?.dismissed);

  const value: TutorialContextType = {
    isActive: !!(state && !state.completed && !state.dismissed),
    flow: state?.flow || null,
    currentStep: state?.current_step || 0,
    currentStepData,
    completed: state?.completed || false,
    dismissed: state?.dismissed || false,
    stepsCompleted: state?.steps_completed || [],
    isLoading,
    isMinimized,
    isHelpMode,
    showHelpMenu,
    experienceResolution,
    multiRoleExperiences,
    saveStatus,
    progressRead,
    activeSteps,
    totalSteps: activeSteps.length,
    resolvedExperience,
    onboardingOffer,
    offerUnavailableReason,
    experienceLabel,
    
    startResolvedTutorial,
    switchExperience,
    completeTutorial,
    completionNotice,
    acknowledgeCompletion,
    helpButtonHidden,
    hideHelpButton,
    nextStep,
    previousStep,
    skipStep,
    jumpToStep,
    dismissTutorial,
    resumeTutorial,
    replayTutorial,
    checkCurrentGate,
    markStepComplete,
    toggleMinimize,
    openHelpMenu,
    closeHelpMenu,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
